import { ReactNode } from 'react'
import styled from 'styled-components'
import DropdownIcon from './DropdownIcon'
import { ToggleCollapseFn } from '../CollapsibleItem/CollapsibleItem'

type ListItemProps = {
  icon?: ReactNode
  title?: ReactNode // this could be a Link if we need it to.
  subtitle?: ReactNode
  rightChildren?: ReactNode // cta / info wrapper
  hasRoundIcon?: boolean
  toggleCollapse?: ToggleCollapseFn
  isCollapsed?: boolean
  className?: string
  onClick?: React.MouseEventHandler<HTMLDivElement>
  slim?: boolean
}

const ListItem = ({
  icon,
  title,
  subtitle,
  rightChildren,
  hasRoundIcon = false,
  toggleCollapse,
  isCollapsed,
  className,
  onClick,
  slim = false,
}: ListItemProps) => {
  const toggleExpand: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (toggleCollapse) toggleCollapse()
  }
  return (
    <ListItemContainer onClick={onClick} className={className}>
      <LeftContainer>
        {icon && <Icon $hasRoundIcon={hasRoundIcon}>{icon}</Icon>}
        <TitleSubtitleContainer $hasMargin={!!icon}>
          {title}
          {subtitle || null}
        </TitleSubtitleContainer>
      </LeftContainer>
      <RightContainer>
        <RightChildren>{rightChildren}</RightChildren>
        {toggleCollapse && (
          <IconButton onClick={(event) => toggleExpand(event)} slim={slim}>
            <RotatedDropdownIcon $isCollapsed={isCollapsed} />
          </IconButton>
        )}
      </RightContainer>
    </ListItemContainer>
  )
}

const ListItemContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 16px 24px;
  border-radius: 16px;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    padding: 12px;
  }
`

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
`

const Icon = styled.div<{ $hasRoundIcon?: boolean }>`
  height: 40px;
  border-radius: ${(props) => (props.$hasRoundIcon ? '50%' : '9px')};
`

const RotatedDropdownIcon = styled(DropdownIcon)<{ $isCollapsed?: boolean }>`
  transform: ${(props) =>
    props.$isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
  transition: transform 0.3s ease-in-out;
`

const TitleSubtitleContainer = styled.div<{ $hasMargin: boolean }>`
  display: flex;
  flex-direction: column;
  margin-left: ${(props) => (props.$hasMargin ? '16px' : 0)};
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    margin-left: ${(props) => (props.$hasMargin ? '8px' : 0)};
  }
`

const IconButton = styled.div<{ slim: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
  width: ${({ slim }) => (slim ? 'auto' : '40px')};
  height: ${({ slim }) => (slim ? 'auto' : '40px')};
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    width: 24px;
    height: 24px;
    margin: 0;
  }
`

const RightChildren = styled.div`
  margin-left: 16px;
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    display: flex;
    flex-direction: column;
    margin-left: 4px;
  }
`

export default ListItem
